import React from 'react';
import { Box } from '@mui/material';
import { mapaDispositivos } from '../../services/consumptionService';
import { eventBus } from '../../services/EventBus';
import { renderArtifactIcon } from '../../utils/artifactIconUtils';

// Tamaño del círculo para dispositivos
const CIRCLE_SIZE = 60;

// Definimos explícitamente la interfaz para las props
interface DeviceTypeImageProps {
  tipo: string;
  tipo_artefacto?: string | null;
  deviceId?: number; // ID del dispositivo para leer de mapaDispositivos como fallback
  deviceIP?: string;
  isAdmin?: boolean;
  onDeviceClick?: (deviceIP: string) => void;
  /** Icono/color desde API Sonos (artefacto) cuando aún no está en tiposArtefactoMap */
  iconOverride?: string | null;
  colorOverride?: string | null;
}

// Estado global para mapa de tipos de artefacto con iconos y colores
// Se actualiza cuando se cargan los tipos desde la API
// Usamos un mapa normalizado (lowercase) para búsquedas case-insensitive
interface TipoArtefactoData {
  icono: string | null;
  color: string | null;
  }
let tiposArtefactoMap: Map<string, TipoArtefactoData> = new Map();
// Mapa adicional para mantener la relación nombre original -> nombre normalizado
let nombreOriginalMap: Map<string, string> = new Map();
// Contador para forzar re-render cuando el mapa cambia
let mapUpdateCounter = 0;

// Función auxiliar para normalizar nombres (trim y lowercase)
const normalizeNombre = (nombre: string): string => {
  return nombre.trim().toLowerCase();
};

// Función para actualizar el mapa de tipos de artefacto
// Si se pasa un array completo, reemplaza todo el mapa
// Si se pasa un solo tipo, lo agrega o actualiza
export const updateTiposArtefactoMap = (tipos: Array<{ nombre: string; icono?: string | null; color_icono?: string | null }> | { nombre: string; icono?: string | null; color_icono?: string | null }) => {
  // Si es un solo objeto, convertirlo a array
  const tiposArray = Array.isArray(tipos) ? tipos : [tipos];
  
  // Si es un array completo (más de 1 elemento o se llama desde Settings/ApplianceTypeDialog inicial),
  // reemplazar todo el mapa
  if (tiposArray.length > 1 || (Array.isArray(tipos) && tipos.length > 0)) {
    tiposArtefactoMap.clear();
    nombreOriginalMap.clear();
  }
  
  tiposArray.forEach(tipo => {
    const nombreNormalizado = normalizeNombre(tipo.nombre);
    tiposArtefactoMap.set(nombreNormalizado, {
      icono: tipo.icono || null,
      color: tipo.color_icono || null
    });
    // Guardar también el nombre original para referencia
    nombreOriginalMap.set(nombreNormalizado, tipo.nombre.trim());
  });
  
  // Incrementar contador para notificar cambios
  mapUpdateCounter++;
  // Emitir evento para que los componentes se actualicen
  if (typeof window !== 'undefined' && window.dispatchEvent) {
    window.dispatchEvent(new CustomEvent('tiposArtefactoMapUpdated', { detail: { counter: mapUpdateCounter } }));
  }
};

// Función para obtener el icono de un tipo de artefacto por nombre (case-insensitive)
export const getIconoForTipoArtefacto = (nombre: string): string | null => {
  if (!nombre) return null;
  const nombreNormalizado = normalizeNombre(nombre);
  return tiposArtefactoMap.get(nombreNormalizado)?.icono || null;
};

// Función para obtener el color de un tipo de artefacto por nombre (case-insensitive)
export const getColorForTipoArtefacto = (nombre: string): string | null => {
  if (!nombre) return null;
  const nombreNormalizado = normalizeNombre(nombre);
  return tiposArtefactoMap.get(nombreNormalizado)?.color || null;
};

// Aseguramos que el componente sea explícitamente tipado con DeviceTypeImageProps
// IMPORTANTE: Usar un estado de actualización para forzar re-render cuando mapaDispositivos cambia
// Esto permite usar React.memo pero aún así re-renderizar cuando sea necesario
const DeviceTypeImage: React.FC<DeviceTypeImageProps> = React.memo(({ tipo, tipo_artefacto, deviceId, deviceIP, isAdmin, onDeviceClick, iconOverride, colorOverride }) => {
  // Estado de actualización para forzar re-render cuando mapaDispositivos cambia
  const [, setUpdateTrigger] = React.useState(0);
  
  // Estado local para tipo_artefacto que se actualiza cuando mapaDispositivos cambia
  const [tipoArtefactoFromMap, setTipoArtefactoFromMap] = React.useState<string | null>(() => {
    if (deviceId && (!tipo_artefacto || tipo_artefacto.trim() === '')) {
      return mapaDispositivos[deviceId]?.tipo_artefacto || null;
    }
    return null;
  });

  // Actualizar estado local cuando cambia el prop tipo_artefacto
  React.useEffect(() => {
    // Si viene tipo_artefacto en props, resetear el estado local
    if (tipo_artefacto && tipo_artefacto.trim() !== '') {
      setTipoArtefactoFromMap(null);
    }
  }, [tipo_artefacto]);

  // Escuchar eventos de actualización de estructura para actualizar tipo_artefacto (optimizado)
  React.useEffect(() => {
    if (!deviceId) return;
    // Solo actualizar desde mapaDispositivos si no hay tipo_artefacto en props
    if (tipo_artefacto && tipo_artefacto.trim() !== '') return;

    const handleEstructuraActualizada = () => {
      // Actualizar desde mapaDispositivos cuando se actualiza la estructura
      const nuevoTipoArtefacto = mapaDispositivos[deviceId]?.tipo_artefacto || null;
      setTipoArtefactoFromMap(prev => {
        // Solo actualizar si cambió para evitar re-renders innecesarios
        if (prev !== nuevoTipoArtefacto) {
          // Forzar re-render incrementando el trigger
          setUpdateTrigger(prev => prev + 1);
          return nuevoTipoArtefacto;
        }
        return prev;
      });
    };
    
    // También escuchar cuando se actualiza un dispositivo específico
    const handleDeviceUpdate = (data: any) => {
      if (data && data.id === deviceId) {
        const nuevoTipoArtefacto = mapaDispositivos[deviceId]?.tipo_artefacto || null;
        setTipoArtefactoFromMap(prev => {
          if (prev !== nuevoTipoArtefacto) {
            setUpdateTrigger(prev => prev + 1);
            return nuevoTipoArtefacto;
          }
          return prev;
        });
      }
    };

    // Actualizar inmediatamente al montar si hay cambios
    handleEstructuraActualizada();

    // Escuchar evento específico para este dispositivo (consolidado desde DeviceListView)
    const unsub = eventBus.on(`device:artifact_type_updated:${deviceId}`, handleEstructuraActualizada);
    // También escuchar evento global de estructura actualizada (menos frecuente)
    const unsubGlobal = eventBus.on('estructura:actualizada', handleEstructuraActualizada);
    // Escuchar actualizaciones de dispositivos individuales
    const unsubDevice = eventBus.on('dispositivo:actualizado', handleDeviceUpdate);

    return () => {
      unsub();
      unsubGlobal();
      unsubDevice();
    };
  }, [deviceId, tipo_artefacto]);

  // Usar tipo_artefacto del prop o del mapa como fallback
  // IMPORTANTE: Leer directamente de mapaDispositivos en cada render
  // updateTrigger se usa como dependencia para forzar re-cálculo cuando mapaDispositivos cambia
  const tipoArtefactoFinal = React.useMemo(() => {
    // Prioridad 1: tipo_artefacto del prop
    if (tipo_artefacto?.trim()) {
      return tipo_artefacto.trim();
    }
    // Prioridad 2: intentar obtener directamente desde mapaDispositivos (más actualizado)
    // Leer directamente para tener siempre el valor más reciente
    if (deviceId && mapaDispositivos[deviceId]?.tipo_artefacto?.trim()) {
      return mapaDispositivos[deviceId]?.tipo_artefacto?.trim() || '';
    }
    // Prioridad 3: tipo_artefacto del estado local (fallback si mapaDispositivos no está disponible aún)
    if (tipoArtefactoFromMap?.trim()) {
      return tipoArtefactoFromMap.trim();
    }
    return '';
  }, [tipo_artefacto, tipoArtefactoFromMap, deviceId]);
  
  // ✅ Verificar periódicamente si mapaDispositivos se ha poblado (para cuando se carga después del montaje)
  React.useEffect(() => {
    if (!deviceId) return;
    if (tipo_artefacto && tipo_artefacto.trim() !== '') return; // Si ya tenemos tipo_artefacto en props, no necesitamos verificar
    
    // Verificar cada 500ms si mapaDispositivos se ha poblado
    const interval = setInterval(() => {
      const nuevoTipoArtefacto = mapaDispositivos[deviceId]?.tipo_artefacto || null;
      if (nuevoTipoArtefacto && nuevoTipoArtefacto.trim() !== '') {
        // Si encontramos tipo_artefacto en mapaDispositivos, actualizar
        setTipoArtefactoFromMap(prev => {
          if (prev !== nuevoTipoArtefacto) {
            setUpdateTrigger(prev => prev + 1);
            return nuevoTipoArtefacto;
          }
          return prev;
        });
        // Limpiar intervalo una vez que encontramos el tipo_artefacto
        clearInterval(interval);
      }
    }, 500);
    
    // Limpiar intervalo después de 30 segundos (tiempo máximo razonable para esperar)
    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 30000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [deviceId, tipo_artefacto]);

  // Estado para forzar re-render cuando el mapa se actualiza
  const [, setMapUpdateTrigger] = React.useState(0);
  
  // Escuchar actualizaciones del mapa global
  React.useEffect(() => {
    const handleMapUpdate = () => {
      setMapUpdateTrigger(prev => prev + 1);
    };
    
    window.addEventListener('tiposArtefactoMapUpdated', handleMapUpdate);
    
    return () => {
      window.removeEventListener('tiposArtefactoMapUpdated', handleMapUpdate);
    };
  }, []);

  // Determinar qué icono o imagen usar según el tipo de artefacto
  const getIconOrImage = React.useMemo(() => {
    const tipoArtefacto = tipoArtefactoFinal;
    
    // Si no hay tipo de artefacto, no mostrar nada (esperar hasta tenerlo)
    // NO mostrar ícono genérico ni fotos de modelos
    if (!tipoArtefacto) {
      return null;
    }
    
    // Obtener icono y color guardados del mapa, si existen
    const iconoGuardado = iconOverride ?? getIconoForTipoArtefacto(tipoArtefacto);
    const colorGuardado = colorOverride ?? getColorForTipoArtefacto(tipoArtefacto);
    
    // Usar utilidad centralizada para renderizar el icono con el color
    const iconContent = renderArtifactIcon(iconoGuardado, tipoArtefacto, 'large', colorGuardado);
    
    if (!iconContent) {
      return null;
    }
    
    return {
      type: 'icon' as const,
      content: iconContent
    };
  }, [tipoArtefactoFinal, iconOverride, colorOverride]);
  
  return (
    <Box
      data-device-icon // Marcar para que el overlay lo excluya
      onClick={(e) => {
        // CRÍTICO: stopPropagation debe estar ANTES de cualquier otra lógica
        e.stopPropagation(); // Evitar que se propague al overlay del Card
        // Solo ejecutar si es admin y hay deviceIP y callback
        if (isAdmin && deviceIP && onDeviceClick) {
          e.preventDefault(); // Solo prevenir comportamiento por defecto si vamos a abrir URL
          onDeviceClick(deviceIP);
        }
        // Si no es admin, no hacer nada pero el stopPropagation previene que se abra el DeviceModal
      }}
      sx={{
        position: 'relative',
        width: CIRCLE_SIZE,
        height: CIRCLE_SIZE,
        borderRadius: '50%',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#1B1D21',
        cursor: isAdmin && deviceIP ? 'pointer' : 'default',
        zIndex: 10, // Asegurar que esté por encima del overlay (zIndex: 1)
        pointerEvents: 'auto', // CRÍTICO: Asegurar que pueda recibir eventos
        // CRÍTICO: Permitir scroll sobre el icono cuando no es admin o no hay IP
        touchAction: (isAdmin && deviceIP) ? 'manipulation' : 'pan-y', // Permitir scroll si no es clickeable
      }}
    >
      {getIconOrImage === null ? (
        // No mostrar nada si no hay tipo_artefacto (esperar hasta tenerlo)
        null
      ) : (
        getIconOrImage.content
      )}
    </Box>
  );
}, (prevProps, nextProps) => {
  // Comparación personalizada para React.memo
  // Solo re-renderizar si las props relevantes cambiaron
  return (
    prevProps.tipo === nextProps.tipo &&
    prevProps.tipo_artefacto === nextProps.tipo_artefacto &&
    prevProps.deviceId === nextProps.deviceId &&
    prevProps.deviceIP === nextProps.deviceIP &&
    prevProps.isAdmin === nextProps.isAdmin &&
    prevProps.iconOverride === nextProps.iconOverride &&
    prevProps.colorOverride === nextProps.colorOverride
  );
});

export default DeviceTypeImage;


